import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { 
  Users, Clock, Trophy, Award, GraduationCap, BarChart3, TrendingUp, 
  UserCheck, UserX, UserPlus, Sparkles, ArrowRight 
} from 'lucide-react';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    api.get('/reports/dashboard').then(r => setStats(r.data));
    api.get('/reports/popular-courses').then(r => setCourses(r.data));
    api.get('/reports/active-users').then(r => setUsers(r.data));
  }, []);

  const statCards = [
    { icon: Users, value: stats.total_students, label: 'Toplam Kullanıcı', color: '#3b82f6', bg: '#eff6ff', borderColor: '#dbeafe' },
    { icon: Clock, value: stats.total_enrollments, label: 'Toplam Kayıt', color: '#f59e0b', bg: '#fffbeb', borderColor: '#fef3c7' },
    { icon: Trophy, value: stats.total_courses, label: 'Eğitim Sayısı', color: '#10b981', bg: '#ecfdf5', borderColor: '#d1fae5' },
    { icon: Award, value: stats.total_certificates, label: 'Sertifika', color: '#8b5cf6', bg: '#f5f3ff', borderColor: '#ede9fe' },
  ];

  return (
    <AdminLayout>
      <div className="dashboard-container">
        
        {/* Karşılama Alanı (Premium Banner) */}
        <div className="welcome-banner">
          <div className="welcome-content">
            <div className="welcome-icon-box">
              <Sparkles size={24} color="#4f46e5" />
            </div>
            <div>
              <h1 className="welcome-title">Eğitim Yönetim Sistemine Hoş Geldiniz</h1>
              <p className="welcome-subtitle">Platformun genel durumunu ve son istatistikleri buradan takip edebilirsiniz. Hızlı işlemler için aşağıdaki butonları kullanın.</p>
            </div>
          </div>
          <div className="quick-actions">
            {[
              { icon: Users, label: 'Kullanıcılar', path: '/yonetim/kullanicilar' }, 
              { icon: GraduationCap, label: 'Eğitimler', path: '/yonetim/egitimler' }, 
              { icon: BarChart3, label: 'Raporlar', path: '/yonetim/raporlar' }
            ].map(({ icon: Icon, label, path }) => (
              <button key={path} className="action-btn" onClick={() => navigate(path)}>
                <Icon size={16} /> 
                <span>{label}</span>
                <ArrowRight size={14} className="action-btn-arrow" />
              </button>
            ))}
          </div>
        </div>

        {/* Kullanıcı Durum Kartları */}
        <div className="status-cards-grid">
          {[
            { label: 'Aktif Kullanıcı', value: stats.active_students, color: '#10b981', bg: '#ecfdf5', icon: UserCheck },
            { label: 'Pasif Kullanıcı', value: stats.passive_students, color: '#f43f5e', bg: '#fff1f2', icon: UserX },
            { label: 'Son 30 Gün Yeni', value: stats.new_students, color: '#4f46e5', bg: '#eef2ff', icon: UserPlus },
          ].map(({ label, value, color, bg, icon: Icon }) => (
            <div key={label} className="stat-card modern-card">
              <div className="stat-icon-wrapper" style={{ background: bg, color: color }}>
                <Icon size={22} strokeWidth={2} />
              </div>
              <div className="stat-info">
                <div className="stat-value" style={{ color: color }}>{value ?? '—'}</div>
                <div className="stat-label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Genel İstatistik Kartları */}
        <div className="general-stats-grid">
          {statCards.map(({ icon: Icon, value, label, color, bg, borderColor }) => (
            <div key={label} className="stat-card modern-card border-accent" style={{ borderBottomColor: borderColor }}>
              <div className="stat-icon-wrapper" style={{ background: bg, color: color }}>
                <Icon size={20} strokeWidth={2.2} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{value?.toLocaleString() || 0}</div>
                <div className="stat-label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tablolar */}
        <div className="tables-grid">
          {[
            { title: 'En Popüler Eğitimler', icon: TrendingUp, data: courses, col1: 'EĞİTİM ADI', col2: 'KATILIMCI', getValue: (item) => item.title, getExtra: (item) => item.participant_count },
            { title: 'En Aktif Kullanıcılar', icon: Users, data: users, col1: 'KULLANICI', col2: 'TAMAMLANAN EĞİTİM', getValue: (item) => item.name, getExtra: (item) => item.completed_courses }
          ].map(({ title, icon: Icon, data, col1, col2, getValue, getExtra }) => (
            <div key={title} className="table-card modern-card">
              <div className="table-header">
                <div className="table-header-icon">
                  <Icon size={16} color="#4f46e5" strokeWidth={2.5} />
                </div>
                <h3 className="table-title">{title}</h3>
              </div>
              <div className="table-wrapper">
                <table className="modern-table">
                  <thead>
                    <tr>
                      {[col1, col2].map(h => (
                        <th key={h} style={{ textAlign: h === col2 ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 ? data.map((item, i) => (
                      <tr key={i}>
                        <td className="main-col">{getValue(item, i)}</td>
                        <td className="extra-col" style={{ textAlign: 'right' }}>
                          <span className="badge">{getExtra(item, i)}</span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="2" className="empty-state">Henüz veri bulunmuyor.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modern Dashboard CSS */}
      <style>{`
        .dashboard-container {
          display: flex;
          flex-direction: column;
          gap: 24px;
          animation: fadeSlideUp 0.5s ease-out;
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Ortak Kart Stili */
        .modern-card {
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #eaecf0;
          box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03), 0 2px 4px -2px rgba(16, 24, 40, 0.03);
          transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
        }
        
        .modern-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 20px -4px rgba(16, 24, 40, 0.08), 0 4px 8px -3px rgba(16, 24, 40, 0.04);
          border-color: #e0e7ff;
        }

        /* Karşılama Alanı */
        .welcome-banner {
          background: linear-gradient(145deg, #ffffff 0%, #f9fafb 100%);
          border-radius: 20px;
          padding: 28px;
          border: 1px solid #eaecf0;
          box-shadow: 0 4px 12px rgba(16, 24, 40, 0.03);
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        @media (min-width: 1024px) {
          .welcome-banner {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        }

        .welcome-content {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .welcome-icon-box {
          background: #eef2ff;
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 10px rgba(79, 70, 229, 0.15);
        }

        .welcome-title {
          margin: 0 0 6px 0;
          font-size: 20px;
          font-weight: 700;
          color: #101828;
          letter-spacing: -0.5px;
        }

        .welcome-subtitle {
          margin: 0;
          font-size: 13.5px;
          color: #667085;
          line-height: 1.5;
          max-width: 500px;
        }

        /* Hızlı Aksiyon Butonları */
        .quick-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #ffffff;
          border: 1px solid #d0d5dd;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 600;
          color: #344054;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
        }
        
        .action-btn-arrow {
          opacity: 0.5;
          transition: transform 0.25s ease;
        }

        .action-btn:hover {
          background: #4f46e5;
          border-color: #4f46e5;
          color: #ffffff;
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(79, 70, 229, 0.2);
        }

        .action-btn:hover .action-btn-arrow {
          opacity: 1;
          transform: translateX(3px);
        }

        /* ----- IZGARA (GRID) SİSTEMLERİ GÜNCELLENDİ ----- */

        /* 3'lü Kart Izgarası (Aktif, Pasif, Yeni Kullanıcı) */
        .status-cards-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        @media (min-width: 768px) {
          .status-cards-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* 4'lü Kart Izgarası (Genel İstatistikler) */
        .general-stats-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        @media (min-width: 640px) {
          .general-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .general-stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        
        /* Tablo Izgarası */
        .tables-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }
        @media (min-width: 1024px) {
          .tables-grid { grid-template-columns: 1fr 1fr; }
        }

        /* ------------------------------------------------ */

        /* İstatistik Kartları */
        .stat-card {
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .border-accent {
          border-bottom-width: 4px;
        }

        .stat-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .stat-card:hover .stat-icon-wrapper {
          transform: scale(1.15) rotate(-5deg);
        }

        .stat-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-value {
          font-size: 26px;
          font-weight: 700;
          color: #101828;
          line-height: 1.1;
          letter-spacing: -0.5px;
        }

        .stat-label {
          font-size: 13px;
          font-weight: 500;
          color: #667085;
        }

        /* Tablolar */
        .table-card {
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .table-header {
          padding: 16px 20px;
          border-bottom: 1px solid #eaecf0;
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(249, 250, 251, 0.5);
        }

        .table-header-icon {
          background: #eef2ff;
          padding: 6px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .table-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          color: #101828;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .modern-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 320px;
        }

        .modern-table th {
          padding: 12px 20px;
          font-size: 11px;
          font-weight: 600;
          color: #667085;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: #f9fafb;
          border-bottom: 1px solid #eaecf0;
        }

        .modern-table td {
          padding: 14px 20px;
          border-bottom: 1px solid #f2f4f7;
          transition: background 0.2s;
        }

        .modern-table tr:last-child td {
          border-bottom: none;
        }

        .modern-table tr:hover td {
          background: #f9fafb;
        }

        .main-col {
          font-size: 13.5px;
          font-weight: 500;
          color: #101828;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .extra-col {
          font-size: 13.5px;
          font-weight: 600;
          color: #475467;
        }

        .badge {
          background: #f2f4f7;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12.5px;
          color: #344054;
        }
        
        .modern-table tr:hover .badge {
          background: #eef2ff;
          color: #4f46e5;
        }

        .empty-state {
          text-align: center;
          padding: 30px !important;
          color: #98a2b3;
          font-style: italic;
          font-size: 13px;
        }
      `}</style>
    </AdminLayout>
  );
}
